import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApiNoConversion } from '~/services/kyClient';

import { queryKeysDataSubscription } from './queryKeys';
import { type MutateContactPointData } from './types';
import { contactPointToApi } from './userKeyedFields';

/** Snake_case: this endpoint is written on the conversion-free client. */
type ContactPointResponse = {
  code: number;
  message: string;
  request_id: UUID | undefined;
};

type UpdateContactPointArguments = {
  contactPointData: MutateContactPointData;
  contactPointId: UUID;
};

/**
 * Updates a contact point.
 *
 * @param {UUID} contactPointId - The unique identifier (UUID) of the contact point that needs to be updated.
 * @param {MutateContactPointData} contactPointData - An object containing the contact point. details to be updated.
 * @returns {Promise<ContactPointResponse>} - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Subscription/operation/update_contact_point_data_subscription_contact_point__id__put
 */
export const updateContactPoint = async (
  contactPointId: UUID,
  contactPointData: MutateContactPointData,
) => {
  const response = await vestigasApiNoConversion
    .put(ENDPOINT.DATA_SUBSCRIPTION.UPDATE_CONTACT_POINT(contactPointId), {
      json: contactPointToApi(contactPointData),
    })
    .json<ContactPointResponse>();

  return response;
};

/**
 * Custom hook for updating a contact point using mutations.
 *
 * @returns {Object} An object containing the mutation function and other properties.
 */
export const useMutationUpdateContactPoint = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      contactPointData,
      contactPointId,
    }: UpdateContactPointArguments) =>
      updateContactPoint(contactPointId, contactPointData),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: queryKeysDataSubscription.getAllContactPoints(),
      });
    },
  });
};
